import { ts } from '@shared/lib/diagTs';

/**
 * Why did the page reload? (2026-09-13, task B3 / decision D3.)
 *
 * Prod 2026-09-12 showed a reload storm — pairs of `RithmicRelayClosed 1001 "page unload"` + a fresh
 * boot every ~10 s — and nothing in the logs said what triggered it: the stale-bundle auto-reload, a
 * consent change, an auth redirect, or the user. Every FE-initiated reload/redirect now goes through
 * `markReload(reason)` first: it logs `page-reload reason=<reason>` through the broker relays' client
 * log channel (forwarded to the BE as `[cqg-fe]` / `[rithmic-fe]` lines, no PII) and leaves a
 * sessionStorage marker that the next boot reads back as `boot after=<reason> gapMs=<n>` once a relay
 * hub is up. A boot with no marker was a user reload / navigation / crash — which is itself the finding.
 */
export type ReloadReason = 'preload-error' | 'consent' | 'sign-out' | 'auth-redirect';

export interface BootMarker {
  reason: ReloadReason;
  /** Milliseconds between the reload being requested and this boot reading the marker. */
  gapMs: number;
  detail?: string;
}

export const BOOT_MARKER_KEY = 'journal.boot.reloadReason';
const REASONS: readonly ReloadReason[] = ['preload-error', 'consent', 'sign-out', 'auth-redirect'];

export type ReloadLogSink = (level: 'info' | 'warn', message: string, detail: string) => void;
const sinks = new Set<ReloadLogSink>();

/** Relays register their BE-forwarding client log here; best-effort (nothing to forward before a hub is up). */
export function registerReloadLogSink(sink: ReloadLogSink): () => void {
  sinks.add(sink);
  return () => { sinks.delete(sink); };
}

/** Record the reason for an imminent FE-initiated reload/redirect — call right BEFORE navigating. */
export function markReload(reason: ReloadReason, detail?: string): void {
  const text = detail ? `reason=${reason} ${detail}` : `reason=${reason}`;
  console.warn(ts(), '[boot] page-reload', text);
  for (const sink of Array.from(sinks)) {
    try { sink('warn', 'page-reload', text); } catch { /* a sink must not block the reload */ }
  }
  try {
    sessionStorage.setItem(BOOT_MARKER_KEY, JSON.stringify({ reason, at: Date.now(), detail }));
  } catch { /* private mode / quota — the console + relay line still went out */ }
}

/** Read and clear the marker the previous page left (null on a plain user reload / fresh visit). */
export function consumeBootMarker(now: number = Date.now()): BootMarker | null {
  try {
    const raw = sessionStorage.getItem(BOOT_MARKER_KEY);
    if (raw === null) return null;
    sessionStorage.removeItem(BOOT_MARKER_KEY);
    const parsed = JSON.parse(raw) as { reason?: unknown; at?: unknown; detail?: unknown };
    if (typeof parsed.reason !== 'string' || !(REASONS as readonly string[]).includes(parsed.reason)) return null;
    const at = typeof parsed.at === 'number' && Number.isFinite(parsed.at) ? parsed.at : now;
    return {
      reason: parsed.reason as ReloadReason,
      gapMs: Math.max(0, now - at),
      ...(typeof parsed.detail === 'string' && parsed.detail ? { detail: parsed.detail } : {}),
    };
  } catch {
    return null;
  }
}

/** The log line a relay emits for a consumed marker once its hub is connected. */
export function describeBoot(marker: BootMarker): string {
  return `after=${marker.reason} gapMs=${marker.gapMs}${marker.detail ? ` ${marker.detail}` : ''}`;
}
